import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

const SEARCH_PATH = '/tim-kiem';

/**
 * Ô tìm kiếm điều khiển URL: mọi truy vấn đều nằm ở /tim-kiem?q=... nên người
 * dùng chia sẻ được link kết quả và nút back hoạt động đúng.
 *
 * `dirty` phân biệt "người dùng đang gõ" với "ô bị xoá do rời trang", tránh
 * việc tự điều hướng ngược lại trang tìm kiếm.
 */
export function SearchBox() {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const onSearchPage = location.pathname === SEARCH_PATH;

	const [value, setValue] = useState(() =>
		onSearchPage ? (searchParams.get('q') ?? '') : '',
	);
	const debounced = useDebouncedValue(value, 400);
	const dirty = useRef(false);

	useEffect(() => {
		if (!onSearchPage) {
			setValue('');
			dirty.current = false;
		}
	}, [onSearchPage]);

	useEffect(() => {
		if (!dirty.current) return;
		const keyword = debounced.trim();

		if (!keyword) {
			if (onSearchPage) navigate(SEARCH_PATH, { replace: true });
			return;
		}
		navigate(`${SEARCH_PATH}?q=${encodeURIComponent(keyword)}`, {
			replace: onSearchPage,
		});
	}, [debounced, navigate, onSearchPage]);

	return (
		<form
			role="search"
			className="relative w-full min-[700px]:w-[min(390px,48vw)]"
			onSubmit={(event) => {
				event.preventDefault();
				const keyword = value.trim();
				if (keyword) {
					navigate(`${SEARCH_PATH}?q=${encodeURIComponent(keyword)}`);
				}
			}}
		>
			<span
				className="pointer-events-none absolute top-2.5 left-4 text-lg text-muted"
				aria-hidden="true"
			>
				⌕
			</span>
			<input
				type="search"
				value={value}
				onChange={(event) => {
					dirty.current = true;
					setValue(event.target.value);
				}}
				placeholder="Tìm kiếm anime..."
				aria-label="Tìm kiếm anime"
				className="w-full rounded-xl border border-line bg-[#15182a] py-3 pr-4.5 pl-11 text-white outline-0 transition placeholder:text-faint focus:border-iris"
			/>
		</form>
	);
}
